import { useEffect, useRef, useState } from "react";
import { Spinner, UploadSimple, CheckCircle, FileXls } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * Admin panel for uploading rpt_004 (Customer Tank Report).
 * Each upload wipes and replaces the delivery-profile collection.
 */
export default function DeliveryProfileImportPanel() {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const inputRef = useRef(null);

  const loadHistory = async () => {
    setLoadingHistory(true);
    try {
      const res = await api.get(`/delivery-profiles/import-history`);
      setHistory(res.data || []);
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => {
    loadHistory();
  }, []);

  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/\.(xls|xlsx)$/i.test(f.name)) {
      toast.error("Please select an .xls or .xlsx file (rpt_004)");
      return;
    }
    setFile(f);
    setSummary(null);
  };

  const runImport = async () => {
    if (!file) return;
    setUploading(true);
    setSummary(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post(`/delivery-profiles/import`, fd, {
        headers: { "Content-Type": undefined },
      });
      setSummary(res.data);
      toast.success(`Imported ${res.data.records} tank records`);
      setFile(null);
      if (inputRef.current) inputRef.current.value = "";
      loadHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Import failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div data-testid="delivery-profile-import-panel">
      <div className="mb-5 border-b border-gray-200 pb-3">
        <h2 className="text-lg font-semibold text-gray-900">Delivery Profile Import</h2>
        <p className="mt-0.5 text-sm text-gray-500">
          Upload the weekly Customer Tank Report (rpt_004.xls). This populates the
          read-only Delivery Profile panel that appears on the New Order page after
          a customer is selected. Each upload fully replaces the previous data.
        </p>
      </div>

      <div className="rounded-md border border-gray-200 bg-[#f9fafb] p-5">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex min-w-0 flex-1 items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-white text-[#1A1A1A] ring-1 ring-gray-200">
              <FileXls size={20} />
            </div>
            <div className="min-w-0">
              <div className="text-sm font-medium text-gray-700">rpt_004 file</div>
              <div
                data-testid="dp-selected-filename"
                className={`truncate text-[15px] ${file ? "text-gray-900" : "text-gray-400"}`}
              >
                {file ? file.name : "No file selected"}
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row">
            <input
              ref={inputRef}
              data-testid="dp-import-file-input"
              type="file"
              accept=".xls,.xlsx"
              onChange={onFileChange}
              className="hidden"
            />
            <button
              data-testid="dp-import-select-button"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              className="inline-flex h-10 items-center justify-center gap-2 rounded-md border border-gray-300 bg-white px-4 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              <UploadSimple size={16} weight="bold" /> Select rpt_004 File
            </button>
            <button
              data-testid="dp-import-run-button"
              onClick={runImport}
              disabled={!file || uploading}
              className="inline-flex h-10 items-center justify-center gap-2 rounded-md bg-[#EB1D25] px-4 text-sm font-semibold text-white hover:bg-[#AF101B] disabled:cursor-not-allowed disabled:bg-gray-300"
            >
              {uploading ? (
                <>
                  <Spinner size={16} className="animate-spin" /> Importing…
                </>
              ) : (
                <>Import Delivery Profiles</>
              )}
            </button>
          </div>
        </div>
      </div>

      {summary && (
        <div
          data-testid="dp-import-summary"
          className="mt-5 rounded-md border border-green-200 bg-green-50/60 p-5"
        >
          <div className="mb-4 flex items-center gap-2 text-green-800">
            <CheckCircle size={20} weight="fill" />
            <span className="text-sm font-semibold">Import complete</span>
          </div>
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-4">
            <Stat label="Tank Records" value={summary.records} testid="dp-stat-records" />
            <Stat label="Unique Customers" value={summary.unique_customers} tone="blue" testid="dp-stat-customers" />
            <Stat label="Unique Products" value={summary.unique_products} tone="amber" testid="dp-stat-products" />
            <Stat label="Zones Seen" value={summary.zones?.length ?? 0} tone="slate" testid="dp-stat-zones" />
          </div>
          <div className="mt-4 grid grid-cols-1 gap-1 border-t border-green-200 pt-3 text-xs text-gray-600 sm:grid-cols-2">
            <div>
              <span className="font-semibold text-gray-700">Import Date &amp; Time:</span>{" "}
              {formatDateTime(summary.finished_at)}
            </div>
            <div>
              <span className="font-semibold text-gray-700">Imported By:</span> {summary.imported_by}
            </div>
          </div>
        </div>
      )}

      <div className="mt-8">
        <h3 className="mb-3 text-sm font-semibold uppercase tracking-wider text-gray-600">Import History</h3>
        <div className="overflow-x-auto rounded-md border border-gray-200 bg-white">
          <table data-testid="dp-import-history" className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-[#f3f4f6] text-left text-xs text-gray-600">
                <th className="px-4 py-2.5 font-semibold">Date &amp; Time</th>
                <th className="px-4 py-2.5 font-semibold">Imported By</th>
                <th className="px-4 py-2.5 font-semibold">File</th>
                <th className="px-4 py-2.5 text-right font-semibold">Tank Records</th>
                <th className="px-4 py-2.5 text-right font-semibold">Unique Customers</th>
              </tr>
            </thead>
            <tbody>
              {loadingHistory ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                    <Spinner size={16} className="mr-2 inline animate-spin" /> Loading…
                  </td>
                </tr>
              ) : history.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-gray-500">No imports yet.</td>
                </tr>
              ) : (
                history.map((h) => (
                  <tr key={h.id} className="border-b border-gray-100 last:border-0 hover:bg-gray-50">
                    <td className="px-4 py-2.5 font-mono text-gray-700">{formatDateTime(h.finished_at)}</td>
                    <td className="px-4 py-2.5 text-gray-700">{h.imported_by}</td>
                    <td className="px-4 py-2.5 text-gray-700">{h.file_name}</td>
                    <td className="px-4 py-2.5 text-right font-mono text-gray-900">{h.records}</td>
                    <td className="px-4 py-2.5 text-right font-mono text-blue-700">{h.unique_customers}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, tone = "slate", testid }) {
  const cls = { slate: "text-gray-900", blue: "text-blue-700", amber: "text-amber-700" }[tone];
  return (
    <div data-testid={testid}>
      <div className="text-xs font-medium uppercase tracking-wider text-gray-500">{label}</div>
      <div className={`mt-1 text-2xl font-semibold ${cls}`}>{value}</div>
    </div>
  );
}

function formatDateTime(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString("en-CA", {
    year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
  });
}
